import React, { useState, useEffect } from 'react';
import { Database, RefreshCw } from 'lucide-react';
import { api } from '../api';
import { ContentSyndicationModal } from '../components/modals/ContentSyndicationModal';

const statusConfig = {
  pending:  { cls: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
  approved: { cls: 'bg-green-100 text-green-800',  label: 'Approved' },
  rejected: { cls: 'bg-red-100 text-red-800',      label: 'Rejected' },
};

const MySyndicationRequestPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const data = await api.request('/syndication-requests');
      setRequests(Array.isArray(data) ? data : []);
    } catch {
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequests(); }, []);

  const statusBadge = (s) => {
    const cfg = statusConfig[s] || { cls: 'bg-gray-100 text-gray-700', label: s };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${cfg.cls}`}>
        {cfg.label}
      </span>
    );
  };

  const hasRequest = requests.length > 0;
  const req = requests[0];

  return (
    <div className="space-y-4" data-testid="my-syndication-request-page">
      <div className="bg-levelup-dark rounded-xl p-4 text-white flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-3">
            <Database size={26} className="text-levelup-accent" />
            My Syndication Request
          </h1>
          <p className="text-gray-300 mt-1 text-sm">
            Track your organisation's LMS content syndication request — only one is allowed per organisation
          </p>
        </div>
        <button
          onClick={fetchRequests}
          className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm text-white transition-colors"
          data-testid="refresh-syndication-request"
        >
          <RefreshCw size={15} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-center py-20">
          <div className="animate-spin w-10 h-10 border-4 border-levelup-accent border-t-transparent rounded-full mx-auto" />
          <p className="text-gray-400 mt-3 text-sm">Loading request…</p>
        </div>
      ) : !hasRequest ? (
        <div className="bg-white rounded-xl border border-gray-200 p-16 text-center">
          <Database size={44} className="text-gray-200 mx-auto mb-4" />
          <p className="text-gray-500 font-medium">No syndication request submitted yet</p>
          <p className="text-gray-400 text-sm mt-1 mb-5">
            Your organisation hasn't requested LMS content syndication. Note: only one request is allowed per organisation.
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="px-5 py-2.5 bg-levelup-accent text-levelup-darker rounded-lg font-semibold hover:bg-levelup-accent-hover transition-colors text-sm"
            data-testid="new-syndication-request-btn"
          >
            Request Content Syndication
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid={`syndication-request-card-${req.id}`}>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="font-bold text-levelup-dark text-lg">{req.organization}</h2>
            {statusBadge(req.status)}
          </div>
          <div className="grid md:grid-cols-2 gap-3 text-sm">
            <div className="bg-gray-50 rounded-lg p-3"><p className="text-gray-500 text-xs mb-1">Contact</p><p className="font-semibold">{req.name}</p><p className="text-gray-500">{req.email}</p></div>
            <div className="bg-gray-50 rounded-lg p-3"><p className="text-gray-500 text-xs mb-1">LMS Platform</p><p className="font-semibold">{req.lms_platform}</p></div>
            <div className="bg-gray-50 rounded-lg p-3"><p className="text-gray-500 text-xs mb-1">SCORM Version</p><p className="font-semibold">{req.scorm_version}</p></div>
            <div className="bg-gray-50 rounded-lg p-3"><p className="text-gray-500 text-xs mb-1">Timeframe</p><p className="font-semibold">{req.timeframe}</p></div>
            <div className="bg-gray-50 rounded-lg p-3"><p className="text-gray-500 text-xs mb-1">Submitted</p><p className="font-semibold">{req.created_at ? new Date(req.created_at).toLocaleDateString() : '—'}</p></div>
          </div>
          {req.admin_notes && (
            <p className="text-sm text-blue-700 bg-blue-50 rounded px-3 py-2 mt-4 border-l-2 border-blue-400"><strong>Notes:</strong> {req.admin_notes}</p>
          )}
        </div>
      )}

      {showModal && (
        <ContentSyndicationModal onClose={() => { setShowModal(false); fetchRequests(); }} />
      )}
    </div>
  );
};

export default MySyndicationRequestPage;
